/* nplus-leasing-deposit.js — <nplus-leasing-deposit> element (VW).
   cfg.requirePayment (from section.settings.require_deposit_payment) selects the flow:
     true  — combined leasing/financing inquiry + card deposit: one submit creates a
             Shopify draft order + Stripe PaymentIntent (cross-linked) via the backend,
             then confirms payment. Uses Stripe.js (global Stripe).
     false — no-payment leasing/financing inquiry: one submit creates the draft order
             only, no Stripe involved.
   Loaded after nplus-leasing-deposit-service.js (window.nplusLeasingDeposit). The host
   section calls ensureStripe() when this element's tab is shown — Stripe can't mount a
   Payment Element inside a hidden panel. */
(function () {
  'use strict';

  var EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  var BILLING_REQUIRED = ['address1', 'city', 'province', 'zip', 'country'];
  // Lease provider mode (ported from nplus-eu, DE route only on VW): eligibility key format
  // per provider key type. Codes are case-sensitive alphanumerics (N38Drk, DFGMPo).
  var KEY_FORMATS = { email: EMAIL_RE, order_code: /^[A-Za-z0-9]{6}$/, token: /^[A-Za-z0-9]{6}$/ };
  // Private-mail domains: soft warning that a personal address was entered instead of the
  // one registered with the provider. Never blocks submit.
  var FREEMAIL_RE = /@(gmail\.com|googlemail\.com|gmx\.[a-z]+|web\.de|outlook\.[a-z]+|hotmail\.[a-z.]+|live\.[a-z.]+|yahoo\.[a-z.]+|t-online\.de|icloud\.com|me\.com)$/i;

  // Stripe card/validation errors carry a shopper-facing, localized message; any other
  // Stripe error (bad key, unknown intent, …) is technical and gets the generic text.
  function stripeError(e) {
    var err = new Error((e && e.code) || 'stripe_error');
    if (e && (e.type === 'card_error' || e.type === 'validation_error') && e.message) err.customerMessage = e.message;
    return err;
  }

  class NplusLeasingDeposit extends HTMLElement {
    connectedCallback() {
      if (this._connected) return; // guard against duplicate listeners on DOM re-insertion
      this._connected = true;
      var cfgEl = this.querySelector('.nplus-leasing-deposit__config');
      try { this.cfg = JSON.parse(cfgEl ? cfgEl.textContent : '{}'); } catch (e) { this.cfg = {}; }

      var self = this;
      this.fields = {};
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-field]'), function (el) {
        self.fields[el.getAttribute('data-nplus-field')] = el;
      });
      this.billing = {};
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-billing]'), function (el) {
        self.billing[el.getAttribute('data-nplus-billing')] = el;
      });
      this.protection = {};
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-protection]'), function (el) {
        self.protection[el.getAttribute('data-nplus-protection')] = el;
      });
      this.consentEl = this.querySelector('[data-nplus-consent]');
      this.termsEl = this.querySelector('[data-nplus-terms]');
      this.submitEl = this.querySelector('[data-nplus-submit]');
      this.statusEl = this.querySelector('[data-nplus-status]');

      if (this.submitEl) this.submitEl.addEventListener('click', this.onSubmit.bind(this));

      // Provider mode (Lease tab): the provider select gates the rest of the form and
      // decides which eligibility key is asked for.
      this.providerEl = this.querySelector('[data-nplus-provider]');
      this.gatedEl = this.querySelector('[data-nplus-provider-gated]');
      this.keyEl = this.querySelector('[data-nplus-field="eligibilityKey"]');
      this.keyMissingEl = this.querySelector('[data-nplus-key-missing]');
      this.keyMissingRowEl = this.querySelector('[data-nplus-key-missing-row]');
      this.keyWarnEl = this.querySelector('[data-nplus-key-warning]');
      if (this.providerEl) {
        this.providerEl.addEventListener('change', this.onProviderChange.bind(this));
        // Start clean: a browser-restored provider would leave the gated block hidden
        // (and Stripe can't mount inside a hidden tab at connect time anyway).
        this.providerEl.value = '';
      }
      if (this.keyMissingEl) this.keyMissingEl.addEventListener('change', this.onKeyMissingChange.bind(this));
      if (this.keyEl) this.keyEl.addEventListener('blur', this.checkKeyDomain.bind(this));

      // Lease tab accessories charge (cfg.chargeAccessories): follow the accessory
      // selection live — card field + pay header + CTA only while paid ones are ticked.
      if (this.cfg.chargeAccessories) {
        this.accPayEl = this.querySelector('[data-nplus-acc-pay]');
        this.accPayTitleEl = this.querySelector('[data-nplus-acc-pay-title]');
        this.peEl = this.querySelector('[data-nplus-pe]');
        this.ctaLabelEl = this.submitEl && this.submitEl.querySelector('.vw-btn-label');
        this.ctaDefault = this.ctaLabelEl ? this.ctaLabelEl.textContent.trim() : '';
        var refresh = this.refreshAccessoriesCharge.bind(this);
        document.addEventListener('change', function (e) { if (e.target && e.target.closest && e.target.closest('nplus-accessories')) refresh(); });
        document.addEventListener('nplus:bike-variant-change', function () { setTimeout(refresh, 0); });
        refresh();
      }
    }

    // Selected wearables (group "wearable" in the accessories config), priced from the
    // page — display only. The backend re-reads product types and Shopify prices and
    // charges only wearables; accessories go into the lease with the bike.
    selectedPaidAccessories() {
      var sel = (window.nplusAccessories && typeof window.nplusAccessories.getSelection === 'function') ? window.nplusAccessories.getSelection() : [];
      var byVariant = {};
      var cfgEl = document.querySelector('nplus-accessories .nplus-acc__config');
      if (cfgEl) {
        try {
          JSON.parse(cfgEl.textContent).forEach(function (a) {
            (a.variants || []).forEach(function (v) { byVariant[String(v.id)] = { title: a.title || '', cents: Number(v.price) || 0, wearable: a.group === 'wearable' }; });
          });
        } catch (e) {}
      }
      var items = [];
      sel.forEach(function (it) {
        if (it.free) return;
        var p = byVariant[String(it.variantId)];
        if (p && p.cents > 0 && p.wearable) items.push(p);
      });
      return items;
    }
    accessoriesCents() { return this.selectedPaidAccessories().reduce(function (s, i) { return s + i.cents; }, 0); }

    // True when this submit takes a card payment: the deposit flow, or the Lease
    // accessories charge while paid accessories are selected.
    isPaying() { return !!this.cfg.requirePayment || (!!this.cfg.chargeAccessories && this.accessoriesCents() > 0); }
    // Minor units the Payment Element is set up for.
    paymentAmount() { return this.cfg.requirePayment ? Math.round(Number(this.cfg.amount)) : this.accessoriesCents(); }

    formatMoney(cents) {
      var cur = String(this.cfg.presentmentCurrency || this.cfg.currency || 'EUR').toUpperCase();
      try { return new Intl.NumberFormat(this.cfg.locale || 'de', { style: 'currency', currency: cur }).format(cents / 100); }
      catch (e) { return cur + ' ' + (cents / 100).toFixed(2); }
    }

    refreshAccessoriesCharge() {
      var cents = this.accessoriesCents();
      var paying = cents > 0;
      var amount = this.formatMoney(cents);
      if (this.accPayEl) this.accPayEl.hidden = !paying;
      if (this.accPayTitleEl) this.accPayTitleEl.textContent = this.t('acc_pay_title', 'Payment for accessories: {amount}').replace('{amount}', amount);
      if (this.peEl) this.peEl.hidden = !paying;
      if (this.ctaLabelEl && !(this.submitEl && this.submitEl.classList.contains('is-busy'))) this.ctaLabelEl.textContent = paying ? this.t('acc_cta', 'Apply for leasing and pay {amount}').replace('{amount}', amount) : this.ctaDefault;
      if (paying) {
        // Mount on first need (only while the Lease panel is visible — ensureStripe is
        // also called by the host when the tab is shown), then keep the amount in sync.
        if (!this.closest('[hidden]')) this.ensureStripe();
        if (this.elements && typeof this.elements.update === 'function') this.elements.update({ amount: cents });
      }
      if (!paying) this.setStatus('');
    }

    keyType() {
      var opt = this.providerEl && this.providerEl.options[this.providerEl.selectedIndex];
      return (opt && opt.getAttribute('data-key-type')) || '';
    }

    onProviderChange() {
      var type = this.keyType();
      if (this.gatedEl) this.gatedEl.hidden = !type;
      this.clearFieldErrors();
      if (!type) return;
      var provider = this.providerEl.value;
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-key-for]'), function (el) {
        var keyProvider = el.getAttribute('data-nplus-key-provider');
        el.hidden = el.getAttribute('data-nplus-key-for') !== type || (!!keyProvider && keyProvider !== provider);
      });
      if (this.keyEl) {
        this.keyEl.value = ''; // a key typed for one provider is never valid for another
        this.keyEl.type = type === 'email' ? 'email' : 'text';
        this.keyEl.autocomplete = type === 'email' ? 'email' : 'off';
        if (type === 'email') this.keyEl.removeAttribute('maxlength'); else this.keyEl.maxLength = this.providerEl.value === 'linexo' ? 40 : 6; // linexo codes vary in length
      }
      if (this.keyWarnEl) this.keyWarnEl.hidden = true;
      // No "don't have it" box for the optional company email; untick it on every switch.
      if (this.keyMissingRowEl) this.keyMissingRowEl.hidden = type === 'email';
      if (this.keyMissingEl && this.keyMissingEl.checked) { this.keyMissingEl.checked = false; this.onKeyMissingChange(); }
      this.ensureStripe(); // held back until the gated block is visible, see ensureStripe()
    }

    onKeyMissingChange() {
      var missing = this.keyMissingEl.checked;
      if (this.keyEl) { this.keyEl.disabled = missing; if (missing) this.keyEl.value = ''; }
      if (missing && this.keyWarnEl) this.keyWarnEl.hidden = true;
    }

    checkKeyDomain() {
      if (!this.keyWarnEl || !this.keyEl) return;
      var v = this.keyEl.value.trim();
      this.keyWarnEl.hidden = !(this.keyType() === 'email' && EMAIL_RE.test(v) && FREEMAIL_RE.test(v));
    }

    setStatus(msg, isError) {
      if (!this.statusEl) return;
      this.statusEl.textContent = msg || '';
      this.statusEl.hidden = !msg;
      this.statusEl.classList.toggle('nplus-leasing__status--error', !!isError);
    }
    genericError() { return this.t('generic_error', 'Something went wrong. Please try again or contact us.'); }
    // Customers never see backend codes (server_error, no_paid_accessories, …): an invalid
    // email goes to its field, Stripe's own card messages (already localized and written for
    // shoppers, e.g. "Your card was declined.") are kept, everything else gets the generic text.
    showSubmitError(err) {
      if (window.console) console.error('[nplus-leasing-deposit] submit failed', err);
      this.setBusy(false);
      var code = err && err.message;
      if (code === 'invalid_email') {
        this.showFieldError('email', this.t('invalid_email', 'Enter a valid email'));
        this.focusFirstError();
        return;
      }
      this.setStatus(err && err.customerMessage ? err.customerMessage : this.genericError(), true);
    }
    setBusy(b) {
      if (!this.submitEl) return;
      this.submitEl.disabled = b;
      this.submitEl.setAttribute('aria-busy', b ? 'true' : 'false');
      this.submitEl.classList.toggle('is-busy', !!b);
      // Swap the label for "Processing…" so customers know to wait (spinner via CSS).
      var label = this.submitEl.querySelector('.vw-btn-label');
      if (label) {
        if (b) { this._idleLabel = label.textContent; label.textContent = this.t('processing', 'Processing…'); }
        else if (this._idleLabel != null) { label.textContent = this._idleLabel; this._idleLabel = null; }
      }
    }
    t(key, fallback) { return (this.cfg && this.cfg.i18n && this.cfg.i18n[key]) || fallback; }

    // The input an error line belongs to: the consent checkbox, or the input/select in the same field wrapper.
    fieldFor(errEl, key) {
      if (key === 'consent') return this.querySelector('[data-nplus-consent]');
      return errEl.parentElement && errEl.parentElement.querySelector('input, select, textarea');
    }
    showFieldError(key, msg) {
      var el = this.querySelector('[data-nplus-error-for="' + key + '"]');
      if (!el) return;
      el.textContent = msg; el.hidden = false;
      var input = this.fieldFor(el, key);
      if (input) {
        if (!el.id) el.id = 'nplus-err-' + key + '-' + Math.random().toString(36).slice(2, 8);
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', el.id);
      }
    }
    clearFieldErrors() {
      var self = this;
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-error-for]'), function (e) {
        e.hidden = true;
        var input = self.fieldFor(e, e.getAttribute('data-nplus-error-for'));
        if (input) { input.removeAttribute('aria-invalid'); if (e.id && input.getAttribute('aria-describedby') === e.id) input.removeAttribute('aria-describedby'); }
      });
    }
    // Errors can sit far above the submit button — bring the first one into view so the click never looks dead.
    focusFirstError() {
      var first = this.querySelector('[aria-invalid="true"]');
      if (!first) return;
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      first.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      try { first.focus({ preventScroll: true }); } catch (e) { first.focus(); }
    }

    // Public: called by the host section when this element's tab becomes visible.
    ensureStripe() {
      if (!this.isPaying()) return; // no-payment inquiry (or no paid accessories yet) — nothing to mount
      // Provider mode: the Payment Element target sits inside the gated block, so hold the
      // mount until a provider is chosen (onProviderChange calls back in).
      if (this.cfg.providerMode && !this.keyType()) return;
      if (this._stripeInit) return;
      this._stripeInit = true;
      if (typeof window.Stripe !== 'function' || !window.nplusLeasingDeposit || !this.cfg.publishableKey) {
        this.setStatus(this.t('payment_unavailable', 'Payment is temporarily unavailable.'), true);
        if (this.submitEl) this.submitEl.disabled = true;
        return;
      }
      // locale drives the Payment Element's language — a German storefront gets a German card form.
      this.stripe = window.Stripe(this.cfg.publishableKey, { locale: this.cfg.locale || 'auto' });
      this.elements = this.stripe.elements({ mode: 'payment', amount: this.paymentAmount(), currency: String(this.cfg.currency || '').toLowerCase(), paymentMethodTypes: ['card'], appearance: { theme: 'stripe' } });
      this.paymentElement = this.elements.create('payment');
      // Mount into THIS element's own target — the Lease and Finance tabs each render an
      // <nplus-leasing-deposit>, so a shared #id would collide.
      var peTarget = this.querySelector('[data-nplus-pe]');
      if (!peTarget) { this.setStatus(this.t('payment_unavailable', 'Payment is temporarily unavailable.'), true); return; }
      this.paymentElement.mount(peTarget);
      this.cardReady = false;
      var self = this;
      this.paymentElement.on('change', function (e) { self.cardReady = !!e.complete; });
    }

    collect() {
      var f = this.fields, b = this.billing, p = this.protection;
      var val = function (el) { return el ? el.value.trim() : ''; };
      var out = {
        productId: this.cfg.productId,
        variantId: this.cfg.variantId,
        customer: { firstName: val(f.firstName), lastName: val(f.lastName), email: val(f.email), phone: val(f.phone) },
        employer: val(f.employer),
        employerOptional: this.cfg.showEmployer === false,
        // data-lead-source: set by the product page when a landing-page teaser link opened it
        // (?leasing_provider=…), so the enquiry counts as a landing-page lead. Lease only.
        source: (this.cfg.type !== 'finance' && this.cfg.source !== 'financing' && this.getAttribute('data-lead-source')) || this.cfg.source || '',
        // Enquiry kind for the app (leasing / finance); lets source carry a lead-source slug instead.
        type: this.cfg.type || undefined,
        tag: (this.cfg.type === 'finance' || this.cfg.source === 'financing') ? 'financing-inquiry' : 'leasing-inquiry',
        consent: !!(this.consentEl && this.consentEl.checked),
        currency: this.cfg.presentmentCurrency,
        locale: this.cfg.locale,
        accessories: (window.nplusAccessories && typeof window.nplusAccessories.getSelection === 'function')
          ? window.nplusAccessories.getSelection() : []
      };
      if (this.cfg.showBilling) {
        out.billing = { address1: val(b.address1), address2: val(b.address2), city: val(b.city), province: val(b.province), zip: val(b.zip), country: val(b.country) };
      }
      if (this.cfg.showProtection) {
        out.protection = { theft: val(p.theft), wear: val(p.wear), inspection: val(p.inspection) };
      }
      // Shape matches the backend's lease object (nplus-deposit-app lib/leasing.js normalizeLease);
      // identical to the German route on nplus-eu.
      if (this.cfg.providerMode) {
        var keyMissing = !!(this.keyMissingEl && this.keyMissingEl.checked);
        var key = keyMissing ? '' : val(f.eligibilityKey);
        // Company email and order codes are optional: blank = not supplied. The
        // Eurorad token stays required unless its box is ticked (validate() flags it).
        var keyOptional = this.keyType() === 'email' || this.keyType() === 'order_code';
        var provider = this.providerEl ? this.providerEl.value : '';
        out.lease = {
          provider: provider,
          key_type: this.keyType(),
          key: key,
          key_supplied: !keyMissing && !(keyOptional && !key),
          cover_preference: null,
          employment_country: 'DE'
        };
      }
      return out;
    }

    validate(payload) {
      this.clearFieldErrors();
      if (this.cfg.providerMode && !payload.lease.provider) {
        this.showFieldError('provider', this.t('required', 'Required'));
        return false; // nothing below the provider select is visible yet
      }
      var ok = true;
      var req = function (cond, key, msg) { if (cond) { ok = false; this.showFieldError(key, msg || this.t('required', 'Required')); } }.bind(this);
      var c = payload.customer;
      req(!c.firstName, 'firstName'); req(!c.lastName, 'lastName');
      if (!c.email) req(true, 'email'); else if (!EMAIL_RE.test(c.email)) { this.showFieldError('email', this.t('invalid_email', 'Enter a valid email')); ok = false; }
      if (this.cfg.showEmployer !== false) req(!payload.employer, 'employer');
      if (this.cfg.showBilling) {
        BILLING_REQUIRED.forEach(function (k) { req(!payload.billing[k], k); });
      }
      if (this.cfg.providerMode) {
        var l = payload.lease;
        if (l.key_supplied) {
          if (!l.key) req(true, 'eligibilityKey');
          else if (l.provider !== 'linexo' && !KEY_FORMATS[l.key_type].test(l.key)) { // linexo: any code accepted (format varies)
            this.showFieldError('eligibilityKey', l.key_type === 'email' ? this.t('invalid_email', 'Enter a valid email') : this.t('invalid_key', 'Enter the 6-character code.'));
            ok = false;
          }
        }
      }
      if (!payload.consent) { this.showFieldError('consent', this.t('consent_required', 'Please agree to the processing of your data to continue.')); ok = false; }
      return ok;
    }

    onSubmit() {
      this.setStatus('');
      if (!window.nplusLeasingDeposit) { this.setStatus(this.genericError(), true); return; }
      var payload = this.collect();
      if (!this.validate(payload)) { this.focusFirstError(); return; }
      if (!this.termsEl || !this.termsEl.checked) { this.setStatus(this.t('accept_terms', 'Please accept the deposit terms.'), true); return; }

      var self = this;

      // VW lease split: the app saves bike, accessories and wearables as separate drafts
      // only for the German Lease tab of an opted-in store, so tell it the market on every
      // Lease submit (with or without the wearables card payment).
      if (!this.cfg.requirePayment && (this.cfg.splitAddOns || this.cfg.chargeAccessories)) payload.market = this.cfg.market || '';

      if (!this.isPaying()) {
        this.setBusy(true);
        window.nplusLeasingDeposit.createLeasingInquiry(payload)
          .then(function () { self.showSuccess(); })
          .catch(function (err) { self.showSubmitError(err); });
        return;
      }

      this.ensureStripe();
      if (!this.stripe || !this.elements) { this.setStatus(this.t('payment_unavailable', 'Payment is temporarily unavailable.'), true); return; }
      if (!this.cardReady) { this.setStatus(this.t('card_incomplete', 'Please complete your card details.'), true); return; }

      // Accessories mode: ask the backend for the accessories charge explicitly; it
      // refuses (rather than charging the flat deposit) where that isn't enabled.
      if (!this.cfg.requirePayment && this.cfg.chargeAccessories) {
        payload.charge = 'accessories';
        payload.market = this.cfg.market || '';
        if (this.elements && typeof this.elements.update === 'function') this.elements.update({ amount: this.accessoriesCents() });
      }

      this.setBusy(true);
      window.nplusLeasingDeposit.createInquiryWithDeposit(payload).then(function (res) {
        // Wearables charge: never confirm an amount other than the one shown on the page
        // (e.g. a product type the page saw differently from Shopify). The draft stays as a
        // lease enquiry and the unconfirmed payment is cancelled by the sweep.
        var shown = self.paymentAmount();
        if (!self.cfg.requirePayment && res && res.amountMinor != null && Number(res.amountMinor) !== shown) {
          var mismatch = new Error('amount_mismatch');
          if (window.console) console.error('[nplus-leasing-deposit] charge ' + res.amountMinor + ' != shown ' + shown);
          throw mismatch;
        }
        return self.elements.submit().then(function (sub) {
          if (sub && sub.error) throw stripeError(sub.error);
          return self.stripe.confirmPayment({
            elements: self.elements,
            clientSecret: res.clientSecret,
            confirmParams: { return_url: self.confirmationUrl(true) },
            redirect: 'if_required'
          });
        });
      }).then(function (result) {
        if (result && result.error) throw stripeError(result.error);
        var pi = result && result.paymentIntent;
        if (pi && pi.id) { window.location.assign(self.confirmationUrl(true) + '&payment_intent=' + encodeURIComponent(pi.id)); return; }
        self.showSuccess();
      }).catch(function (err) { self.showSubmitError(err); });
    }

    // Absolute URL of the deposit confirmation page, locale-prefixed (e.g. /de/pages/…).
    isLeaseWearables() { return !this.cfg.requirePayment && !!this.cfg.chargeAccessories; }

    // withQuery: the wearables payment of a lease enquiry adds ?kind=lease so the confirmation
    // page shows lease wording right away (Stripe appends &payment_intent after 3-D Secure).
    confirmationUrl(withQuery) {
      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      return window.location.origin + root + 'pages/deposit-confirmation' + (withQuery ? (this.isLeaseWearables() ? '?kind=lease' : '?kind=deposit') : '');
    }

    showSuccess() {
      var title = this.t('success_title', 'Inquiry & deposit received');
      var body = this.t('success_body', 'Thank you — your deposit is confirmed and your inquiry is in. Our team will be in touch shortly.');
      this.innerHTML = '<div class="nplus-leasing__success"><h3>' + title + '</h3><p>' + body + '</p></div>';
    }
  }

  if (!customElements.get('nplus-leasing-deposit')) {
    customElements.define('nplus-leasing-deposit', NplusLeasingDeposit);
  }
})();
